import hash from 'object-hash';
import _ from 'lodash';
import { SchemaMutationInput, TableDataMutationInput } from '@/main/store/@types/store.d';
import { ListStoreBase, RowDetail } from '@/main/store/@types/list.d';
import { columnsToCamelCase, Row, Table, cellNames } from '@/main/utils/table';
import {
	UPDATE_BASIC_FILTER, UPDATE_BASIC_TABLE_SCHEMA, UPDATE_BASIC_TABLE_DATA, STORE_TABLE,
	ROW_DETAIL_LOADING, RESET_FILTER, ROW_DETAIL, START_LOADING, END_LOADING, COLUMN_VISIBLE, SAVE_SORT,
} from '@/main/store/listEditTypes';
import { Status } from '@/main/store/@types/page';
import { dataMapper } from '@/main/utils/dataMapper';
import { objectToCamelCaseShallow } from '@/main/utils/stringHelper';

export const mutations = {
	[UPDATE_BASIC_FILTER](state: ListStoreBase, data: any) {
		if (!state.filter) throw Error('Undefined Filter');

		if (data.DropDownList) {
			state.dropdownList = {
				..._.defaultTo(state.dropdownList, {}),
				...data.DropDownList,
			};
		}

		if (data.ValidationRules) {
			if (!_.isEmpty(data.ValidationRules)) {
				state.validationRules = data.ValidationRules;
			}
			delete data.ValidationRules;
		}

		const filter = _.omitBy(data.Filter, _.isNil);
		dataMapper(state.filter, filter);
		state.status = Status.ok;
		state.table.emptyTextKey = 'TableMsgRunSearch';
	},
	/**
	 * Updates table schema of table `tableName` of component `stateKey`.
	 *
	 * @param {Object} state
	 * @param {Object} data - table data.
	 * @param {String} tableName - name of the table, default `[STORE_TABLE]`.
	 */
	[UPDATE_BASIC_TABLE_SCHEMA](state: ListStoreBase, { data, tableName = STORE_TABLE }: SchemaMutationInput) {
		const table = _.get(state, tableName) as Table;
		table.columns = columnsToCamelCase(data.Columns);
		table.cellNames = cellNames(table);
		table.gridId = data.GridId;
		table.operations = data.Operations;
		state.operations = data.Operations;
		table.hasIdColumn = data.HasIdColumn;
		table.status = Status.ok;
	},
	/**
	 * Updates table data of table `tableName` of component `stateKey`.
	 *
	 * @param {Object} state
	 * @param {Object} data - table data.
	 * @param {String} tableName - name of the table, default `[STORE_TABLE]`.
	 */
	[UPDATE_BASIC_TABLE_DATA](state: ListStoreBase, { data, tableName = STORE_TABLE, isEditable = false }: TableDataMutationInput) {
		const table = _.get(state, tableName) as Table;

		table.status = Status.ok;
		table.rows = data.map((row: Row) => objectToCamelCaseShallow(row));
		isEditable && (table.hash = hash(table.rows));
	},

	[START_LOADING](state: ListStoreBase, tableName?: string) {
		(tableName ? _.get(state, tableName) as Table : state).loadingCount += 1;
	},

	[END_LOADING](state: ListStoreBase, tableName?: string) {
		(tableName ? _.get(state, tableName) as Table : state).loadingCount -= 1;
	},

	/**
	 * `Object.assign(state.rowDetail, data);`, throws error if rowDetail doesn't exist.
	 *
	 * @param {Object} state.
	 * @param {String} data.
	 */
	[ROW_DETAIL](state: ListStoreBase, { data }: { data: any }) {
		if (state.rowDetail === null) throw Error('rowDetail can\'t be null');

		Object.assign(state.rowDetail, data);
	},
	[SAVE_SORT](state: ListStoreBase, { data, tableName }: { data: { columnId: any; order: string }, tableName: string; }) {
		(tableName ? _.get(state, tableName) as Table : state.table).sort = data;
	},
	[ROW_DETAIL_LOADING](state: ListStoreBase, loading: boolean) {
		(<RowDetail>state.rowDetail).loadingCount += loading ? 1 : -1;
	},

	/**
	 * Resets filter - Overwrite Filter state with Init.Filter state
	 */
	[RESET_FILTER](state: any) {
		_.assign(state.filter, _.cloneDeep(state.init.filter));
	},
	[COLUMN_VISIBLE](state: ListStoreBase, { tableName = 'table', columnIndex, visible }: { tableName?: string, columnIndex: number, visible: boolean }) {
		(_.get(state, tableName) as Table).columns[columnIndex].isVisible = visible;
	},
	updateTableRow(state: ListStoreBase, { tableName = STORE_TABLE, rowId, row }: { tableName: string, rowId: number, row?: Row }) {
		const { table } = state;
		const filteredRows = table.rows.filter((item) => item.cells[table.cellNames!.idColumn!] !== rowId);
		table.rows = (row) ? [...filteredRows, row] : filteredRows;
	},
};
